/**
 * 点击事件参数采集器
 * 从 MouseEvent 提取元素信息/坐标
 * 对齐 docs/埋点数据.md event.click 字段结构
 */

import type { ClickEventParams } from '../types/event'
import { DATA_ATTRS } from '../core/constants'

/**
 * 从点击事件中采集参数
 * @param event MouseEvent 对象
 * @returns ClickEventParams 对象
 */
export function collectClickEvent(event: MouseEvent): ClickEventParams {
  const target = event.target as HTMLElement | null

  if (!target) {
    return {
      clickPosition: { x: event.clientX, y: event.clientY },
    }
  }

  // 向上查找带有 track 数据属性的元素
  const trackedElement = findTrackedElement(target)
  const elementInfo = getElementInfo(trackedElement || target)

  // 获取点击场景
  const clickScene = findClickScene(target)

  return {
    clickEleId: elementInfo.eleId,
    clickEleName: elementInfo.eleName,
    clickEleParams: elementInfo.eleParams,
    clickPosition: { x: event.clientX, y: event.clientY },
    clickScene,
  }
}

/**
 * 判断元素是否需要追踪
 * @param element HTML 元素
 * @returns 是否需要追踪
 */
export function shouldTrackClick(element: HTMLElement | null): boolean {
  if (!element) return false

  // 检查元素或其父元素是否有 track 数据属性
  const trackedElement = findTrackedElement(element)
  if (trackedElement) return true

  // 检查是否为可交互元素
  const interactiveTags = ['A', 'BUTTON', 'INPUT', 'SELECT', 'TEXTAREA']
  if (interactiveTags.includes(element.tagName)) return true

  // 检查是否有 onclick 或 role="button"
  if (element.onclick || element.getAttribute('role') === 'button') return true

  return false
}

/**
 * 向上查找带有 track 数据属性的元素
 */
function findTrackedElement(element: HTMLElement): HTMLElement | null {
  let current: HTMLElement | null = element
  const maxDepth = 5 // 最大向上查找层数

  for (let i = 0; i < maxDepth && current; i++) {
    if (current.hasAttribute(DATA_ATTRS.ELE_ID) || current.hasAttribute(DATA_ATTRS.ELE_NAME)) {
      return current
    }
    current = current.parentElement
  }

  return null
}

/**
 * 获取元素基本信息
 * 返回数值型 ID（hash）以对齐文档 number 类型
 */
function getElementInfo(element: HTMLElement): {
  eleId: number | undefined
  eleName: string | undefined
  eleParams: Record<string, unknown> | undefined
} {
  // 从 data 属性或 id 获取元素标识
  const rawId = element.getAttribute(DATA_ATTRS.ELE_ID) || element.id || undefined
  const eleId = rawId ? simpleHash(rawId) : undefined

  // 元素名称
  const eleName =
    element.getAttribute(DATA_ATTRS.ELE_NAME) ||
    element.getAttribute('aria-label') ||
    element.getAttribute('title') ||
    element.innerText?.slice(0, 50)?.trim() ||
    element.tagName.toLowerCase()

  // 解析元素绑定参数
  const eleParams = parseEleParams(element)

  return { eleId, eleName, eleParams }
}

/**
 * 解析元素绑定参数
 * 从 data-tracker-ele-params 属性中解析 JSON 对象
 * data-tracker-ele-params 可能在目标元素或其父元素或祖先元素上定义
 * data-tracker-ele-params 可能一个或多个
 * data-tracker-ele-params 的值将被解析为 JSON 对象
 * 合并时子元素属性优先级高于父元素
 * 
 * 例如：
 * <div data-tracker-ele-params='{"a": 1, "b": 2}'>
  <span data-tracker-ele-params='{"b": 3, "c": 4}'>
    <button>Click</button>
  </span>
</div>
点击 button 时，最终合并结果为 { a: 1, b: 3, c: 4 }（子元素的 b: 3 覆盖了父元素的 b: 2）。
 */
function parseEleParams(element: HTMLElement): Record<string, unknown> | undefined {
  // 收集从目标元素到祖先元素的所有 ele-params
  const paramsList: Record<string, unknown>[] = []
  let current: HTMLElement | null = element

  while (current) {
    const raw = current.getAttribute(DATA_ATTRS.ELE_PARAMS)
    if (raw) {
      try {
        const parsed = JSON.parse(raw)
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
          paramsList.push(parsed as Record<string, unknown>)
        }
      } catch {
        // 解析失败则跳过该节点
      }
    }
    current = current.parentElement
  }

  // 无有效参数则返回 undefined
  if (paramsList.length === 0) return undefined

  // 从后向前合并，确保子元素属性覆盖父元素属性
  // paramsList[0] 是目标元素，paramsList[n] 是最远祖先
  const merged: Record<string, unknown> = {}
  for (let i = paramsList.length - 1; i >= 0; i--) {
    Object.assign(merged, paramsList[i])
  }

  return merged
}

/**
 * 简单字符串 hash 转数值
 */
function simpleHash(str: string): number {
  let hash = 0
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i)
    hash = ((hash << 5) - hash) + char
    hash = hash & hash // Convert to 32bit integer
  }
  return Math.abs(hash)
}

/**
 * 从元素向上查找 data-tracker-click-scene 属性值
 * @param element 目标元素
 * @returns 最近的 click-scene 属性值，未找到则返回空字符串
 */
function findClickScene(element: HTMLElement | null): string {
  let current: HTMLElement | null = element

  while (current) {
    const clickScene = current.getAttribute(DATA_ATTRS.CLICK_EVENT_SCENE)
    if (clickScene) return clickScene
    current = current.parentElement
  }

  return ''
}
